import { CalculatedPropertyState } from "@animech/axiom-configurator";
import { Path } from "@animech/axiom-format";
import { arraysAreEqual, resolveArrayIndices } from "@animech/axiom-utility";
import { z } from "zod";
import { Property } from "src/common";

// General preferences

export type Preference = PrimitivePreference | PropertyPreference;

export type PreferenceValue = z.infer<typeof PreferenceValueSchema>;

export const PreferenceValueSchema = z.union([
	z.string(),
	z.number(),
	z.boolean(),
	z.null(),
]);

export type PreferenceMapping = Record<string, Preference | Preference[]>;

export function isPreferenceValue(value: unknown): value is PreferenceValue {
	const type = typeof value;
	return (
		type === "string" ||
		type === "number" ||
		type === "boolean" ||
		value === null
	);
}

// Primitive preferences

export interface PrimitivePreference<
	TKey extends string = string,
	TValue extends PreferenceValue = PreferenceValue,
> {
	type: "primitive";
	preferenceKey: TKey;
	validateValue(value: unknown): value is TValue;
}

export function makePrimitivePreference<
	TKey extends string,
	TValue extends PreferenceValue,
>(
	preference: Omit<PrimitivePreference<TKey, TValue>, "type">,
): PrimitivePreference<TKey, TValue> {
	return { type: "primitive", ...preference };
}

// Property preferences

export type PropertyPreferenceKey = {
	key: string;
	compatibleKeys?: string[];
};

export interface PropertyPreferenceKeyContext<TValue, TPath extends Path> {
	property: Property<TValue, TPath>;
	templateIndex: number;
	get<T extends Property>(property: T): CalculatedPropertyState<T>;
}

export interface PropertyPreference<
	TValue = unknown,
	TPath extends Path = Path,
> {
	type: "property";
	template?: Property<unknown[]>;
	property: Property<TValue, TPath>;
	preferenceKey(
		value: TValue,
		context: PropertyPreferenceKeyContext<TValue, TPath>,
	): PropertyPreferenceKey;
}

export function makePropertyPreference<
	TValue extends PreferenceValue | undefined,
	TPath extends Path,
>(
	preference: Omit<PropertyPreference<TValue, TPath>, "type">,
): PropertyPreference<TValue, TPath> {
	return { type: "property", ...preference };
}

export function propertyBelongsToPropertyPreference(
	property: Property,
	preference: PropertyPreference,
): boolean {
	const resolvedPath = resolveArrayIndices(
		preference.property.getPath(),
		property.getPath(),
	);
	return arraysAreEqual(resolvedPath, property.getPath());
}
